import {
  Box,
  Card,
  CardContent,
  Separator,
  Skeleton,
  Text,
  type ThemeKeys,
  Tooltip,
} from "@prismicio/editor-ui"
import type { ReactNode } from "react"

import { formatValue, type Unit } from "./usageUtils"

interface UsageDetailsCardProps {
  usage: { included?: number; used: number }
  overage?: { included?: number; used: number }
  unit?: Unit
}

export function UsageDetailsCard(props: UsageDetailsCardProps) {
  const { usage, overage, unit } = props

  const usageDetails = (
    <>
      <UsageDetailLine
        label="Used"
        value={usage.used}
        color="green8"
        unit={unit}
      />
      {usage.included && (
        <UsageDetailLine
          label="Included"
          value={usage.included}
          color="green4"
          unit={unit}
        />
      )}
    </>
  )

  const overageDetails =
    overage && overage.included ? (
      <>
        <UsageDetailLine
          label="Used overage"
          value={overage.used}
          color="amber10"
          unit={unit}
        />
        <UsageDetailLine
          label="Allowed overage"
          value={overage.included}
          color="amber4"
          unit={unit}
        />
        <Separator />
        <UsageDetailLine
          label="Total used"
          value={usage.used + overage.used}
          unit={unit}
        />
        {usage.included ? (
          <UsageDetailLine
            label="Total included"
            value={usage.included + overage.included}
            unit={unit}
          />
        ) : null}
      </>
    ) : null

  return (
    <UsageDetailsCardTemplate
      usageDetails={usageDetails}
      overageDetails={overageDetails}
    />
  )
}

export function UsageDetailsCardSkeleton() {
  const valueSkeleton = <Skeleton width={80} height={16} color="grey5" />
  return (
    <UsageDetailsCardTemplate
      usageDetails={
        <>
          <UsageDetailLineTemplate
            label="Used"
            color="green8"
            rightContent={valueSkeleton}
          />
          <UsageDetailLineTemplate
            label="Included"
            color="green4"
            rightContent={valueSkeleton}
          />
        </>
      }
    />
  )
}

interface UsageDetailLineProps
  extends Pick<UsageDetailLineTemplateProps, "label" | "color"> {
  value: number
  unit: Unit
}

function UsageDetailLine(props: UsageDetailLineProps) {
  const { label, value, color, unit } = props

  const humanReadableValue = formatValue(value, { unit })

  const humanReadableTooltip = formatValue(value, {
    unit,
    notation: "standard",
  })

  return (
    <UsageDetailLineTemplate
      label={label}
      color={color}
      rightContent={
        <Tooltip content={humanReadableTooltip} sideOffset={4}>
          <Text variant="small" color="grey11">
            {humanReadableValue}
          </Text>
        </Tooltip>
      }
    />
  )
}

interface UsageDetailsCardTemplateProps {
  usageDetails: ReactNode
  overageDetails?: ReactNode
}

function UsageDetailsCardTemplate(props: UsageDetailsCardTemplateProps) {
  const { usageDetails, overageDetails } = props
  return (
    <Card padding={16} color="grey2" variant="outlined">
      <CardContent gap={16}>
        {usageDetails}
        {overageDetails && (
          <>
            <Separator />
            {overageDetails}
          </>
        )}
      </CardContent>
    </Card>
  )
}

interface UsageDetailLineTemplateProps {
  label: string
  color?: ThemeKeys<"color">
  rightContent?: ReactNode
}

function UsageDetailLineTemplate(props: UsageDetailLineTemplateProps) {
  const { label, color = "transparent", rightContent } = props

  return (
    <Box justifyContent="space-between">
      <Box gap={8} alignItems="center">
        <Box backgroundColor={color} width={12} height={12} borderRadius={6} />
        <Text variant="smallBold">{label}</Text>
      </Box>
      {rightContent}
    </Box>
  )
}
